import axios from "axios";
import { buildP2PStats } from "./stats";
import { ALLOWED_FIATS, ALLOWED_SIDES, DEFAULT_P2P_LIMIT } from "./types";
import { formatRequestError } from "./errors";

const BYBIT_ENDPOINT = "/api/bybit-p2p/fiat/otc/item/online";
const BYBIT_BACKEND_ENDPOINT = "/api/p2p/bybit/secure";
const BYBIT_FALLBACK_ENABLED = import.meta.env.VITE_USE_BACKEND_BYBIT === "true";

/**
 * @param {Object} options
 * @param {"BRL"|"BOB"} options.fiat
 * @param {"BUY"|"SELL"} options.side
 * @param {number} [options.size]
 * @param {number} [options.page]
 * @returns {Promise<import("./types").P2PQuote>}
 */
export async function fetchBybitP2P({
  fiat,
  side,
  size = DEFAULT_P2P_LIMIT,
  page = 1,
}) {
  const normalizedFiat = String(fiat || "").toUpperCase();
  if (!ALLOWED_FIATS.includes(normalizedFiat)) {
    throw new Error("Fiat inválido para Bybit P2P");
  }
  const normalizedSide = String(side || "").toUpperCase();
  if (!ALLOWED_SIDES.includes(normalizedSide)) {
    throw new Error("Side inválido para Bybit P2P");
  }

  const payload = {
    userId: "",
    tokenId: "USDT",
    currencyId: normalizedFiat,
    payment: [],
    side: normalizedSide,
    size,
    page,
    amount: "",
    authMaker: false,
    canTrade: false,
  };

  const extractItems = (data) => {
    const items = Array.isArray(data?.result?.items)
      ? data.result.items
      : [];
    const prices = items
      .map((item) => Number(item?.price))
      .filter((price) => Number.isFinite(price));
    if (!prices.length) {
      const error = new Error("Não foi possível obter anúncios válidos da Bybit");
      error.payload = data;
      throw error;
    }
    return { prices, items };
  };

  const postBybit = async (endpoint) => {
    const response = await axios.post(endpoint, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return extractItems(response.data);
  };

  const buildStats = ({ prices, items }) =>
    buildP2PStats({
      exchange: "bybit",
      fiat: normalizedFiat,
      side: normalizedSide,
      prices,
      raw: import.meta.env.MODE === "development" ? items : undefined,
    });

  try {
    const result = await postBybit(BYBIT_ENDPOINT);
    return buildStats(result);
  } catch (error) {
    if (BYBIT_FALLBACK_ENABLED) {
      try {
        const fallbackResult = await postBybit(BYBIT_BACKEND_ENDPOINT);
        return buildStats(fallbackResult);
      } catch (fallbackError) {
        throw new Error(
          formatRequestError({
            exchange: "bybit",
            fiat: normalizedFiat,
            side: normalizedSide,
            fallback: "Indisponível",
            error: fallbackError,
          })
        );
      }
    }
    throw new Error(
      formatRequestError({
        exchange: "bybit",
        fiat: normalizedFiat,
        side: normalizedSide,
        fallback: "Não foi possível obter anúncios válidos da Bybit",
        error,
      })
    );
  }
}
